import { Injectable, Injector, Type, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

import { ModalRef, MODAL_DATA, MODAL_REF } from './modal-ref';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface ModalOptions<D = unknown> {
  data?: D;
  backdropDismissable?: boolean;
  size?: ModalSize;
}

export interface ActiveModal {
  component: Type<unknown>;
  options: ModalOptions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: ModalRef<any>;
  injector: Injector;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly parentInjector = inject(Injector);
  private readonly blockScroll = inject(ScrollStrategyOptions).block();
  private readonly router = inject(Router);
  private readonly _activeModal = signal<ActiveModal | null>(null);

  readonly activeModal = this._activeModal.asReadonly();
  readonly showModal = computed(() => this._activeModal() !== null);

  constructor() {
    // A route change must not strand a modal on the next screen: a non-dismissable modal
    // (e.g. ConcludeModal) would otherwise trap the student on the next page with no way out.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.dismiss());
  }

  open<R = unknown>(component: Type<unknown>, options: ModalOptions = {}): ModalRef<R> {
    const ref = new ModalRef<R>();

    const injector = Injector.create({
      providers: [
        { provide: MODAL_REF, useValue: ref },
        { provide: MODAL_DATA, useValue: options.data ?? null },
      ],
      parent: this.parentInjector,
    });

    this._activeModal.set({ component, options, ref, injector });
    this.blockScroll.enable();

    ref.closed.subscribe({
      complete: () => {
        if (this._activeModal()?.ref === ref) this.close();
      },
    });

    return ref;
  }

  dismiss(): void {
    this._activeModal()?.ref.dismiss();
  }

  private close(): void {
    this._activeModal.set(null);
    this.blockScroll.disable();
  }
}
